import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { keineErlaubt } from '../../domain/kommentararten';
import { commentCount, isTaskDone, totalMax, totalScore } from '../../domain/scoring';
import { toNumber } from '../../domain/points';
import { usePoints, useT } from '../../i18n';
import { BOGEN_SCROLL_MARKE } from './bogenShot';
import { useNachObenBeiWechsel } from './nachObenRollen';
import { SchlafKnopf } from '../schlaf/SchlafKnopf';
import { tippen, type LoeschScharf } from '../sheets/loeschScharf';
import {
  selectAssessment,
  selectStation,
  selectTasks,
  useAppStore,
} from '../../store/useAppStore';
import { Icon } from '../../ui/Icon';
import { useRollrand } from '../../ui/rollrand';
import styles from './Bogen.module.css';
import { AbzugKommentar, TaskCard } from './TaskCard';

/** Der Bogen selbst: Fallvignette obenauf, darunter die Aufgaben. */
export function Bogen() {
  const t = useT();
  const station = useAppStore(selectStation);
  const tasks = useAppStore(selectTasks);
  const assessment = useAppStore(selectAssessment);
  const openSheet = useAppStore((s) => s.openSheet);
  // Nach dem Absenden bleibt der Bogen sichtbar, aber unveränderlich.
  const locked = useAppStore((s) => s.submitted[s.candidateId] != null);
  const candidateId = useAppStore((s) => s.candidateId);
  /**
   * Die zwei Schatten sagen, dass oben oder unten noch etwas liegt (Vorgabe des Nutzers,
   * 2026-09-13). Ohne sie sieht ein Bogen, der gerade an einer Kartengrenze abschneidet, aus
   * wie ein vollständiger — und eine Aufgabe bliebe unbewertet, weil niemand sie vermutet.
   *
   * ⚠️ **Die Hooks stehen vor dem `EmptySheet`-Ausstieg.** React lässt eine Bedingung davor
   * nicht zu; der leere Bogen zahlt dafür einen Horcher, den er nicht braucht.
   */
  const rollRef = useRef<HTMLDivElement>(null);
  const rand = useRollrand(rollRef, tasks);
  // Bei jedem Wechsel von Station oder Prüfling wieder an den Anfang — s. `nachObenRollen.ts`.
  // Der Bogen wird dabei **nicht** neu gemountet; ohne das erbte der nächste Prüfling die
  // Scrollstellung des vorigen (und nach einem Wechsel-Abzug genau die, die `captureBogen`
  // wiederhergestellt hat).
  useNachObenBeiWechsel(rollRef, station.id + '/' + candidateId);
  // Während des Abzugs verschwinden die Schatten: `bogenShot` fotografiert genau dieses
  // Element, und ein eingefrorener Verlauf mitten im Bild sähe aus wie ein Fehler im Bogen.
  const imAbzug = useAppStore((s) => s.shotStamp) != null;
  // Der Stationskommentar steht im Abzug unter der letzten Aufgabe — s. `TaskCard.tsx::AbzugKommentar`.
  const stationKommentarAn = useAppStore((s) => s.stationComments && s.kommentarArten.text);
  const stationsText =
    imAbzug && stationKommentarAn ? (assessment.comments[station.id]?.text.trim() ?? '') : '';
  if (tasks.length === 0) return <EmptySheet />;

  return (
    // Die Marke sagt dem Abzug beim Absenden, welches Element der Bogen ist. Sie steht hier
    // und nicht als Klassenname, damit ein Umbenennen im CSS-Modul sie nicht still mitnimmt.
    <div
      ref={rollRef}
      className={`sc ${styles.scroll}`}
      data-oben={rand.oben && !imAbzug}
      data-unten={rand.unten && !imAbzug}
      data-abzug={imAbzug}
      {...{ [BOGEN_SCROLL_MARKE]: '' }}
    >
      <div className={`column ${styles.list}`}>
        {station.vignette && (
          <button
            type="button"
            className={`tap ${styles.vignette}`}
            onClick={() => openSheet({ kind: 'vignette' })}
          >
            <span className={styles.vignetteIcon}>
              <Icon name="info" size={18} />
            </span>
            <span className={styles.vignetteText}>
              <span className={styles.vignetteLabel}>{t.bogen.vignette}</span>
              <span className={styles.vignetteTeaser}>{station.vignette.teaser}</span>
            </span>
            <span className={styles.vignetteOpen}>
              <span>{t.bogen.open}</span>
              <Icon name="chevronRight" size={15} />
            </span>
          </button>
        )}

        {tasks.map((task, i) => (
          <TaskCard
            key={task.id}
            task={task}
            index={i}
            assessment={assessment}
            locked={locked}
            imAbzug={imAbzug}
          />
        ))}
        {stationsText && <AbzugKommentar titel={t.bogen.stationComment} text={stationsText} />}
      </div>
    </div>
  );
}

/**
 * Wie lange der Abgabeknopf scharf bleibt — **eine** Sekunde (Vorgabe des Nutzers).
 *
 * Kürzer als die zwei Sekunden des Papierkorbs: dort sucht der Prüfer die richtige Zeile,
 * hier zielt er auf einen Knopf, den er ohnehin drücken wollte.
 */
const ABGABE_FRIST_MS = 1000;

/** Die Kennung, unter der `tippen` den Abgabeknopf führt — es gibt nur einen. */
const ABGABE = 'abgabe';

/** Fußzeile: Fortschritt und der Weg zum Absenden. */
export function BogenFooter() {
  const t = useT();
  const points = usePoints();
  const tasks = useAppStore(selectTasks);
  const assessment = useAppStore(selectAssessment);
  const openSheet = useAppStore((s) => s.openSheet);
  const record = useAppStore((s) => s.submitted[s.candidateId]);
  const summeZeigen = useAppStore((s) => s.bogenAnzeige.summe);
  /**
   * Der Abgabeknopf verlangt **zwei** Tipper (Vorgabe des Nutzers, 2026-09-13). Ein einziger
   * Fehlgriff auf dem liegenden iPad leitete sonst die Abgabe ein — und die ist das Ende der
   * Bewertung, nicht ein Schritt darin. Dieselbe geprüfte Mechanik wie beim Papierkorb der
   * Prüfungsauswahl (`sheets/loeschScharf.ts`), nur mit kürzerer Frist: hier zielt der Prüfer
   * bewusst, dort sucht er.
   */
  const stationComments = useAppStore((s) => s.stationComments);
  /**
   * Welche Kommentararten zugelassen sind (`*_comments`, Auftrag des Nutzers, 2026-09-18).
   *
   * ⚠️ **Zwei Schalter, nicht einer.** `station_comments_enabled` sagt, *ob* es den Kasten zur
   * Station gibt; diese fünf sagen, *was* darin steht. Sind alle fünf aus, hilft der Kasten
   * nicht mehr — er öffnete ein Blatt ohne Reiter.
   */
  const arten = useAppStore((s) => s.kommentarArten);
  const station = useAppStore(selectStation);
  // Wie viel am Stationskommentar hängt — dieselbe Zählung wie beim Aufgabenkommentar.
  const stationKommentare = commentCount(assessment.comments[station.id], arten);
  const [scharf, setScharf] = useState<LoeschScharf>(null);
  const scharfUhr = useRef<number | null>(null);

  useEffect(() => () => {
    if (scharfUhr.current !== null) window.clearTimeout(scharfUhr.current);
  }, []);

  const abgebenTippen = () => {
    const folge = tippen(scharf, ABGABE, Date.now(), ABGABE_FRIST_MS);
    if (scharfUhr.current !== null) window.clearTimeout(scharfUhr.current);
    if (folge.aktion === 'loeschen') {
      setScharf(null);
      openSheet({ kind: 'submit' });
      return;
    }
    setScharf(folge.scharf);
    // Etwas mehr als die Frist, damit der Balken sichtbar ausläuft, ehe der Knopf zurückspringt.
    scharfUhr.current = window.setTimeout(() => {
      scharfUhr.current = null;
      setScharf(null);
    }, ABGABE_FRIST_MS + 80);
  };

  const done = tasks.filter((t) => isTaskDone(t, assessment)).length;
  const withComments = tasks
    .map((t, i) => (commentCount(assessment.comments[t.id], arten) > 0 ? i + 1 : null))
    .filter((n): n is number => n != null);

  const label =
    t.bogen.progress(done, tasks.length) +
    (withComments.length ? ' · ' + t.bogen.commentsOn(withComments.join(', ')) : '');

  const max = toNumber(totalMax(tasks));
  // Ohne Gesamtpunkte (`show_sum_points`) misst der Balken erledigte Aufgaben statt Punkte —
  // sonst verriete er den Anteil, den die Kopfzeile gerade nicht zeigt.
  const pct = !summeZeigen
    ? tasks.length === 0
      ? 0
      : Math.round((done / tasks.length) * 100)
    : max === 0
      ? 0
      : Math.round((toNumber(totalScore(tasks, assessment)) / max) * 100);

  // ⚠️ **Die Fusszeile bleibt auch ohne Aufgaben stehen.** Sie trägt seit dem 2026-09-13 den
  // Schlafknopf, und der gilt in jeder Phase; verschwände die Zeile beim leeren Bogen, wäre er
  // genau dort weg. Fortschritt und „Absenden" hängen weiter an den Aufgaben.
  return (
    <div className={styles.footer}>
      <div className={`column ${styles.footerRow}`}>
        <div className={styles.progress}>
          {tasks.length > 0 && (
            <>
              <div className={styles.progressLabel}>{label}</div>
              <div className={styles.progressTrack}>
                <div className={styles.progressBar} style={{ width: pct + '%' }} />
              </div>
            </>
          )}
        </div>
        {/* Der Kommentar zur **Station** — nicht zu einer Aufgabe. Er steht hier unten, weil
            er die Station als Ganzes betrifft und beim Abgeben das Letzte ist, was der Prüfer
            noch festhält (Vorgabe des Nutzers, 2026-09-13). Abschaltbar in den Einstellungen. */}
        {stationComments && !keineErlaubt(arten) && tasks.length > 0 && (
          <button
            type="button"
            className={`tap ${styles.stationComment}`}
            data-has={stationKommentare > 0}
            onClick={() => openSheet({ kind: 'stationComment' })}
          >
            <Icon name="comment" size={16} />
            <span>{stationKommentare > 0 ? t.bogen.stationCommentOn(stationKommentare) : t.bogen.stationComment}</span>
          </button>
        )}
        <SchlafKnopf gestalt="fuss" />
        {tasks.length === 0 ? null : record ? (
          <button
            type="button"
            className={`tap ${styles.submit}`}
            data-done="true"
            onClick={() => openSheet({ kind: 'locked' })}
          >
            <Icon name="check" size={17} />
            <span>
              {t.bogen.submitted}
              {summeZeigen && <> · {points(record.total)} / {points(record.max)}</>}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className={`tap ${styles.submit}`}
            // ⚠️ **Blasser, nicht gesperrt.** Solange Aufgaben offen sind, soll der Knopf nicht
            // einladen — verwehren darf er die Abgabe aber nicht: ein Prüfling kann abbrechen,
            // und dann ist ein unvollständiger Bogen das richtige Ergebnis.
            data-offen={done < tasks.length}
            data-scharf={scharf !== null}
            style={{ '--abgabeFrist': `${ABGABE_FRIST_MS}ms` } as CSSProperties}
            onClick={abgebenTippen}
          >
            <span>{scharf ? t.bogen.submitArm : t.bogen.submit}</span>
            <Icon name={scharf ? 'warning' : 'arrowRight'} size={17} />
          </button>
        )}
      </div>
    </div>
  );
}

function EmptySheet() {
  const t = useT();
  const openSheet = useAppStore((s) => s.openSheet);
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}>
        <Icon name="sheet" size={26} />
      </span>
      <div className={styles.emptyTitle}>{t.bogen.emptyTitle}</div>
      <div className={styles.emptyNote}>{t.bogen.emptyNote}</div>
      <button
        type="button"
        className={`tap ${styles.emptyAction}`}
        onClick={() => openSheet({ kind: 'setup' })}
      >
        <Icon name="pin" size={16} />
        <span>{t.bogen.chooseStation}</span>
      </button>
    </div>
  );
}
